import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Check, Zap, DollarSign, Mail, X, Lock, ShieldCheck, BadgeCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState({});
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');

  useEffect(() => {
    fetchPricing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricing(response.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching pricing:', error);
      }
    }
  };

  const handlePurchase = async (tier) => {
    // Show email collection modal first
    setSelectedTier(tier);
    setShowEmailModal(true);
  };

  const proceedToPayment = async () => {
    if (!customerEmail || !customerEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    const tier = selectedTier;
    setShowEmailModal(false);
    setLoading({ [tier]: true });

    try {
      const amount = tier === 'pro' ? 19.0 : 1.0;
      const returnUrl = `${window.location.origin}/success`;
      const cancelUrl = `${window.location.origin}/pricing`;
      
      const response = await axios.post(`${API}/create-payment`, {
        return_url: returnUrl,
        cancel_url: cancelUrl,
        amount,
        tier,
        customer_email: customerEmail
      });
      
      if (response.data.approval_url) {
        // Store email locally for the success page
        localStorage.setItem('customer_email', customerEmail);
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Payment error:', error);
      }
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading({ [tier]: false });
    }
  };

  if (!pricing) return <div className="flex items-center justify-center min-h-screen"><div className="text-green-500">Loading...</div></div>;

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />
      
      <div className="content-wrapper">
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="pricing-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-green-400" data-testid="back-home">
                ← Back to Home
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 glow-text" data-testid="pricing-title">
              CHOOSE YOUR TIER
            </h1>
            <p className="text-xl text-green-300" data-testid="pricing-subtitle">
              Start with Basic or unlock AI-powered features with Pro
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Basic Tier */}
            <Card className="card-tech p-8 flex flex-col" data-testid="basic-tier-card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <DollarSign className="w-12 h-12 text-green-500 mb-2" />
                  <h2 className="text-3xl font-bold text-green-400">{pricing.basic.name}</h2>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-green-500 glow-text">${pricing.basic.price}</div>
                  <div className="text-green-400 text-sm">one-time</div>
                </div>
              </div>

              <div className="flex-1 mb-6">
                <h3 className="text-lg font-semibold text-green-400 mb-4">Includes:</h3>
                <ul className="space-y-3">
                  {pricing.basic.features.map((feature, idx) => (
                    <li key={feature} className="flex items-start" data-testid={`basic-feature-${idx}`}>
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-green-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => handlePurchase('basic')}
                disabled={loading.basic}
                className="btn-secondary w-full py-6 text-lg"
                data-testid="buy-basic-button"
              >
                {loading.basic ? 'Processing...' : 'Get Basic - $1'}
              </Button>
            </Card>

            {/* Pro Tier */}
            <Card className="card-tech p-8 flex flex-col border-2 border-green-500 pulse-glow" data-testid="pro-tier-card">
              <div className="absolute top-4 right-4">
                <span className="bg-green-500 text-black px-3 py-1 rounded-full text-sm font-bold">RECOMMENDED</span>
              </div>
              
              <div className="flex items-center justify-between mb-6 mt-6">
                <div>
                  <Zap className="w-12 h-12 text-green-500 mb-2" />
                  <h2 className="text-3xl font-bold text-green-400">{pricing.pro.name}</h2>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-green-500 glow-text">${pricing.pro.price}</div>
                  <div className="text-green-400 text-sm">one-time</div>
                </div>
              </div>

              <div className="flex-1 mb-6">
                <h3 className="text-lg font-semibold text-green-400 mb-4">Everything in Basic, PLUS:</h3>
                <ul className="space-y-3">
                  {pricing.pro.features.slice(1).map((feature, idx) => (
                    <li key={feature} className="flex items-start" data-testid={`pro-feature-${idx}`}>
                      <Check className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-green-300 font-semibold">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => handlePurchase('pro')}
                disabled={loading.pro}
                className="btn-primary w-full py-6 text-lg"
                data-testid="buy-pro-button"
              >
                {loading.pro ? 'Processing...' : 'Get Pro - $19'}
              </Button>
            </Card>
          </div>

          <div className="mt-16 text-center">
            <p className="text-green-400 text-lg mb-4">Secure payment via PayPal or Credit/Debit Card</p>
            <p className="text-green-500/80 text-sm mb-8">One-time payment - Instant access - MIT Licensed</p>

            {/* Trust & Security Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-6" data-testid="trust-badges">
              <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-green-500/30 rounded-lg" data-testid="badge-ssl">
                <Lock className="w-4 h-4 text-green-500" />
                <div className="text-left">
                  <div className="text-green-400 text-xs font-bold">256-BIT SSL</div>
                  <div className="text-green-500/70 text-[10px]">Encrypted Checkout</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-green-500/30 rounded-lg" data-testid="badge-paypal">
                <ShieldCheck className="w-4 h-4 text-green-500" />
                <div className="text-left">
                  <div className="text-green-400 text-xs font-bold">PAYPAL VERIFIED</div>
                  <div className="text-green-500/70 text-[10px]">Buyer Protection</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-green-500/30 rounded-lg" data-testid="badge-norton">
                <BadgeCheck className="w-4 h-4 text-green-500" />
                <div className="text-left">
                  <div className="text-green-400 text-xs font-bold">SECURED</div>
                  <div className="text-green-500/70 text-[10px]">Site Protected</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-green-500/30 rounded-lg" data-testid="badge-pci">
                <ShieldCheck className="w-4 h-4 text-green-500" />
                <div className="text-left">
                  <div className="text-green-400 text-xs font-bold">PCI DSS</div>
                  <div className="text-green-500/70 text-[10px]">Level 1 Compliant</div>
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-black/50 border border-green-500/30 rounded-lg" data-testid="badge-money-back">
                <Check className="w-4 h-4 text-green-500" />
                <div className="text-left">
                  <div className="text-green-400 text-xs font-bold">GUARANTEED</div>
                  <div className="text-green-500/70 text-[10px]">Instant Delivery</div>
                </div>
              </div>
            </div>
          </div>

          {/* Email Collection Modal */}
          {showEmailModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="email-modal-overlay">
              <Card className="card-tech p-8 max-w-md mx-4 w-full relative" data-testid="email-modal">
                <button onClick={() => setShowEmailModal(false)} className="absolute top-4 right-4 text-green-400 hover:text-green-300" data-testid="close-email-modal">
                  <X className="w-5 h-5" />
                </button>
                
                <div className="text-center mb-6">
                  <Mail className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-green-400">
                    {selectedTier === 'pro' ? 'Get Pro Edition - $19' : 'Get Basic Edition - $1'}
                  </h3>
                  <p className="text-green-300 mt-2">
                    Enter your email to receive access instructions after payment.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-green-400 text-sm font-semibold mb-2">Your Email Address</label>
                    <Input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-black/50 border-green-500/30 text-green-300 h-12"
                      onKeyDown={(e) => e.key === 'Enter' && proceedToPayment()}
                      autoFocus
                      data-testid="customer-email-input"
                    />
                    <p className="text-green-500/60 text-xs mt-1">
                      We'll send your access instructions here. Works with any email, even if paying by card.
                    </p>
                  </div>

                  <Button
                    onClick={proceedToPayment}
                    className="btn-primary w-full py-5 text-lg"
                    data-testid="proceed-to-payment-button"
                  >
                    Continue to Payment
                  </Button>
                </div>

                <p className="text-green-500/50 text-xs text-center mt-4">
                  You'll be redirected to PayPal. Pay with PayPal account or credit/debit card.
                </p>

                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-green-500/20" data-testid="modal-security-reassurance">
                  <Lock className="w-3 h-3 text-green-500" />
                  <span className="text-green-500/70 text-[11px]">Secured by 256-bit SSL · PayPal Verified</span>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;